import { ChartContainer } from '@/components/charts/chartContainer'
import { ChartTooltip } from '@/components/charts/chartTooltip'
import { calculateBorrowRateAtUtilization } from '@/utils/borrowMarkets/calculateBorrowRateAtUtilization'
import { getBorrowInterestRateModelChartConfig } from '@/utils/borrowMarkets/getBorrowInterestRateModelChartConfig'
import { cn } from '@/utils/cn'
import type { BorrowMarketAssetData } from '@/utils/internalAppSdk'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  type LabelProps,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

type BorrowMarketInterestRateModel = NonNullable<BorrowMarketAssetData['interestRateModel']>

interface Props {
  interestRateModel: BorrowMarketInterestRateModel
  currentBorrowRate?: number
  currentUtilizationRate?: number
  className?: string
}

interface ChartPoint {
  utilizationRate: number
  borrowRate: number
}

interface ChartMouseState {
  activePayload?: Array<{ payload?: ChartPoint }>
}

interface CartesianLabelViewBox {
  x?: number
  y?: number
  width?: number
  height?: number
}

const CHART_MARGIN = { top: 8, right: 8, bottom: 16, left: 0 }
const Y_AXIS_WIDTH = 96
const Y_AXIS_LABEL_OFFSET_X = 10
const CURRENT_BADGE_HEIGHT = 22
const CURRENT_BADGE_PADDING_X = 8
const CURRENT_BADGE_EDGE_GAP = 4
const TOOLTIP_GUIDE_STROKE = 'rgb(var(--body-foreground))'
const TOOLTIP_GUIDE_STROKE_OPACITY = 0.4

export const BorrowMarketInterestRateModelChart = ({
  interestRateModel,
  currentBorrowRate,
  currentUtilizationRate,
  className,
}: Props) => {
  const [activePoint, setActivePoint] = useState<ChartPoint | null>(null)
  const { points, yMax } = useMemo(
    () => getBorrowInterestRateModelChartConfig({ interestRateModel, currentBorrowRate }),
    [interestRateModel, currentBorrowRate]
  )
  const currentBorrowRateOnCurve = useMemo(
    () =>
      currentUtilizationRate === undefined
        ? undefined
        : calculateBorrowRateAtUtilization(interestRateModel, currentUtilizationRate),
    [interestRateModel, currentUtilizationRate]
  )
  const hasCurrentPoint = currentBorrowRateOnCurve !== undefined && currentUtilizationRate !== undefined

  return (
    <ChartContainer className={cn('h-60', className)}>
      <ResponsiveContainer>
        <AreaChart
          data={points}
          margin={CHART_MARGIN}
          onMouseMove={(state: ChartMouseState) => setActivePoint(state.activePayload?.[0]?.payload ?? null)}
          onMouseLeave={() => setActivePoint(null)}
        >
          <defs>
            <linearGradient id="borrowInterestRateModelFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="utilizationRate"
            type="number"
            domain={[0, 1]}
            ticks={[0, 1]}
            tickLine={false}
            tickMargin={12}
            label={{ value: 'Utilization', position: 'insideBottom', offset: -12 }}
            tickFormatter={(value) => formatDecimalToPercentage(value, { fractionDigits: 0 })}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            type="number"
            domain={[0, yMax]}
            ticks={[0, yMax / 2, yMax]}
            tickLine={false}
            tickMargin={0}
            label={<YAxisLabel label="Borrow APY" />}
            tickFormatter={(value) => (value === 0 ? '0' : formatDecimalToPercentage(value, { fractionDigits: 0 }))}
          />
          <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
          {activePoint && (
            <>
              <ReferenceLine
                segment={[
                  { x: activePoint.utilizationRate, y: 0 },
                  { x: activePoint.utilizationRate, y: activePoint.borrowRate },
                ]}
                stroke={TOOLTIP_GUIDE_STROKE}
                strokeOpacity={TOOLTIP_GUIDE_STROKE_OPACITY}
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
              />
              <ReferenceLine
                segment={[
                  { x: 0, y: activePoint.borrowRate },
                  { x: activePoint.utilizationRate, y: activePoint.borrowRate },
                ]}
                stroke={TOOLTIP_GUIDE_STROKE}
                strokeOpacity={TOOLTIP_GUIDE_STROKE_OPACITY}
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
              />
            </>
          )}
          <Area
            dataKey="borrowRate"
            type="monotone"
            isAnimationActive={false}
            stroke="rgb(var(--accent))"
            strokeWidth={3}
            fill="url(#borrowInterestRateModelFill)"
            dot={false}
            activeDot={{
              r: 4,
              fill: 'rgb(var(--accent))',
              stroke: 'rgb(var(--accent))',
              strokeWidth: 0,
            }}
          />
          {hasCurrentPoint && (
            <>
              <ReferenceLine
                segment={[
                  { x: 0, y: currentBorrowRateOnCurve },
                  { x: currentUtilizationRate, y: currentBorrowRateOnCurve },
                ]}
                stroke="rgb(var(--accent))"
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
                label={
                  <CurrentBorrowRateBadge
                    borrowRate={currentBorrowRateOnCurve}
                    label={formatDecimalToPercentage(currentBorrowRateOnCurve)}
                    yMax={yMax}
                  />
                }
              />
              <ReferenceLine
                segment={[
                  { x: currentUtilizationRate, y: 0 },
                  { x: currentUtilizationRate, y: currentBorrowRateOnCurve },
                ]}
                stroke="rgb(var(--accent))"
                strokeDasharray="3 3"
                ifOverflow="extendDomain"
                label={
                  <CurrentUtilizationBadge
                    label={formatDecimalToPercentage(currentUtilizationRate, { fractionDigits: 0 })}
                    utilizationRate={currentUtilizationRate}
                  />
                }
              />
              <ReferenceDot
                x={currentUtilizationRate}
                y={currentBorrowRateOnCurve}
                r={6}
                fill="rgb(var(--accent))"
                stroke="rgb(var(--bg-page))"
                strokeWidth={4}
                ifOverflow="extendDomain"
                label={<CurrentNowLabel utilizationRate={currentUtilizationRate} />}
              />
            </>
          )}
          <Tooltip cursor={false} content={({ payload }) => <BorrowRateModelTooltip data={payload?.[0]?.payload} />} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

const CurrentNowLabel = ({ viewBox, utilizationRate }: LabelProps & { utilizationRate: number }) => {
  const point = getCartesianViewBox(viewBox)

  if (!point || point.x === undefined || point.y === undefined) return null

  const centerX = point.width === undefined ? point.x : point.x + point.width / 2
  const isNearLeftEdge = utilizationRate < 0.08
  const isNearRightEdge = utilizationRate > 0.92
  const x = isNearLeftEdge ? centerX + 10 : isNearRightEdge ? centerX - 10 : centerX
  const y = point.y < 22 ? point.y + (point.height ?? 0) + 14 : point.y - 10
  const textAnchor = isNearLeftEdge ? 'start' : isNearRightEdge ? 'end' : 'middle'

  return (
    <text x={x} y={y} fill="rgb(var(--accent))" fontSize={12} fontWeight={500} textAnchor={textAnchor}>
      Now
    </text>
  )
}

const YAxisLabel = ({ viewBox, label }: LabelProps & { label: string }) => {
  const axis = getCartesianViewBox(viewBox)

  if (!axis || axis.x === undefined || axis.y === undefined || axis.width === undefined || axis.height === undefined) {
    return null
  }

  const x = axis.x + Y_AXIS_LABEL_OFFSET_X
  const y = axis.y + axis.height / 2

  return (
    <text
      x={x}
      y={y}
      fill="rgb(var(--body-foreground))"
      fontSize={12}
      textAnchor="middle"
      dominantBaseline="central"
      transform={`rotate(-90 ${x} ${y})`}
    >
      {label}
    </text>
  )
}

const CurrentBorrowRateBadge = ({
  viewBox,
  borrowRate,
  label,
  yMax,
}: LabelProps & { borrowRate: number; label: string; yMax: number }) => {
  const line = getCartesianViewBox(viewBox)

  if (!line || line.y === undefined) return null

  const badgeWidth = getCurrentBadgeWidth(label)
  const axisX = line.x === undefined ? Y_AXIS_WIDTH : line.x
  const x = Math.max(0, axisX - badgeWidth)
  const borrowRateRatio = yMax === 0 ? 0 : borrowRate / yMax
  const y =
    borrowRateRatio > 0.9
      ? line.y + CURRENT_BADGE_EDGE_GAP
      : borrowRateRatio < 0.1
        ? line.y - CURRENT_BADGE_HEIGHT - CURRENT_BADGE_EDGE_GAP
        : line.y - CURRENT_BADGE_HEIGHT / 2

  return <CurrentAxisBadge x={x} y={y} label={label} />
}

const CurrentUtilizationBadge = ({
  viewBox,
  label,
  utilizationRate,
}: LabelProps & { label: string; utilizationRate: number }) => {
  const line = getCartesianViewBox(viewBox)

  if (!line || line.x === undefined || line.y === undefined || line.height === undefined) return null

  const badgeWidth = getCurrentBadgeWidth(label)
  const x = utilizationRate > 0.9 ? line.x - badgeWidth : utilizationRate < 0.1 ? line.x : line.x - badgeWidth / 2

  return <CurrentAxisBadge x={x} y={line.y + line.height + CURRENT_BADGE_EDGE_GAP} label={label} />
}

const CurrentAxisBadge = ({ x, y, label }: { x: number; y: number; label: string }) => {
  const width = getCurrentBadgeWidth(label)

  return (
    <g>
      <rect x={x} y={y} width={width} height={CURRENT_BADGE_HEIGHT} rx={11} fill="rgb(var(--accent))" />
      <text
        x={x + width / 2}
        y={y + CURRENT_BADGE_HEIGHT / 2}
        fill="rgb(var(--bg-page))"
        fontSize={12}
        fontWeight={500}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {label}
      </text>
    </g>
  )
}

const getCurrentBadgeWidth = (label: string) => label.length * 7 + CURRENT_BADGE_PADDING_X * 2

const getCartesianViewBox = (viewBox: LabelProps['viewBox']) => {
  if (!viewBox) return null
  if (!('x' in viewBox) && !('y' in viewBox)) return null

  const { x, y, width, height } = viewBox as CartesianLabelViewBox

  if (x === undefined && y === undefined) return null

  return { x, y, width, height }
}

const BorrowRateModelTooltip = ({ data }: { data: ChartPoint | undefined }) => {
  if (!data) return null

  return (
    <ChartTooltip>
      <div className="flex items-center justify-between gap-2">
        <span>Utilization:</span>
        <span className="font-medium">{formatDecimalToPercentage(data.utilizationRate, { fractionDigits: 0 })}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span>Borrow APY:</span> <span className="font-medium"> {formatDecimalToPercentage(data.borrowRate)}</span>
      </div>
    </ChartTooltip>
  )
}
